/**
 * 이상한 방 - CPU 좌석의 판단 (찾는 봇 · 자기 구획을 고치는 봇 · 난이도 표)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 9.1절(술래 AI는 "못 찾는 척하는" 문제 - `(N, p, q)`) ·
 *            9.3절(CPU는 서버가 스스로 액션을 발행하는 플레이어 - 새 코드 경로 0) · 9.4절(CPU는 개발 도구다)
 *            로드맵 모델 2.6절(CPU) · `core/rng.ts` 머리말(소비량 고정 계약)
 *
 * ## 순수 함수다 - 리듀서와 같은 세 조건
 *
 * `cpuDecide(state, playerId, rng)`는 상태를 읽고 `PlayerEvent[]`를 돌려준다. 상태를 바꾸지 않고, 시간을 읽지
 * 않고, 난수는 받은 `rng`에서만 뽑는다. 결과는 호스트가 **사람 이벤트와 같은 큐**에 넣으므로 리듀서는 누가
 * 넣었는지 모른다(9.3절). 그래서 봇이 `already-found`를 안 쏘는 것도, 관전 중에 조용한 것도 리듀서의 규칙이
 * 아니라 이 파일의 예의다 - 리듀서는 봇이 헛짓을 해도 사람과 똑같이 거절한다.
 *
 * ## 판단마다 난수 소비량이 고정이다
 *
 * 한 판단은 **항상 `CPU_DECISION_ROLLS`개**를 소비한다. 분기(관전 중이라 아무것도 안 함 · 원래 방 안 · 찾음 ·
 * 오답 · 관찰)에 따라 소비량이 달라지면 `?botFind=`를 바꿨을 때 그 뒤 수열 전체가 어긋나 "확률만 바꿨는데
 * 봇이 다른 것을 쏜다"가 된다(`rng.ts`의 `chance(0)`도 소비하는 것과 같은 이유). 세 롤을 먼저 뽑고 그 다음에 본다.
 *
 * 그 계약은 **수색 판단의 것**이다. 바꾸기 페이즈는 롤을 하나도 뽑지 않는다 - 스트림이 라운드 단위라(`matchHost.botRngFor`)
 * 바꾸기에서 먹은 롤만큼 수색이 밀리고, 그 개수가 사람의 커밋 시각에 달리면 같은 시드가 다른 판이 된다(`cpuDecide`의 0번).
 *
 * ## 스케줄은 호스트의 것이다
 *
 * 이 함수는 **한 번의 판단**이다. "N초마다"는 상태에 없는 정보(봇이 마지막으로 판단한 시각)를 요구하므로
 * 순수 함수가 스스로 알 수 없다. 대신 `cpuDecisionsDue(before, after, profile)`가 "이 틱에 판단 경계를 몇 번
 * 넘었는가"를 돌려주고, 호스트(`net/matchHost.ts`, M2)가 그 수만큼 `cpuDecide`를 부른다. 봇 하나의 난수
 * 스트림은 `botStreamSeed` = `${seed}:bot:${id}:r${round}` 하나이고, 라운드 시작에 `createRng`로 한 번 만들어
 * 라운드 내내 이어 쓴다 - 그래야 같은 시드 + 같은 틱 열이면 봇도 같은 것을 쏜다(M1 규칙 7).
 *
 * ## 이웃의 바꾸기는 "다 했다" 한 장이다 (M11 규칙 7)
 *
 * 이웃도 자기 구획을 갖고 그 구획도 이상한 곳으로 채워진다. 그런데 이웃이 `applyChange`를 한 건씩 보내게 하면
 * **두 번째 생성 경로**가 생긴다 - 후보를 고르고 동사를 붙이고 배분을 맞추는 일을 `changeGen`이 이미 하고 있고,
 * 그 일을 여기서 다시 하면 사람이 만든 변화와 이웃이 만든 변화의 질이 갈린다. 그래서 이웃의 판단은
 * **[다 했다] 한 장**이고, 비운 예산은 마감 채움(`matchPhases.fillZone`)이 그 구획 안에서 생성기로 채운다 -
 * 결과는 "이웃이 자기 구획을 고쳤다"와 정확히 같고, 결정론도 생성기의 시드 하나에서 나온다.
 * 커밋을 실제로 보내는 이유는 그것이 **사람과 같은 큐·같은 이벤트**이기 때문이다(9.3절) - 리듀서는
 * 이웃의 커밋과 사람의 커밋을 구분하지 않는다.
 */

import type { Rng } from '../../core/rng'
import { MS_PER_SECOND } from '../../core/time'
import type { PlayerId } from '../../core/types'
import type { Change, ChangeDifficulty } from '../changeGen'
import type { HitTarget } from '../hunt'
import type { Tuning } from '../tuning'
import { canUseDesk } from './matchDesk'
import { canEnterOrigin } from './matchOrigin'
import { findableUnfound, playerOf } from './matchSelectors'
import { isActiveHunter } from './matchShoot'
import type { BotProfile, MatchState, PlayerEvent } from './matchTypes'

/* --- 난이도 표 ------------------------------------------------------------------- */

export type BotDifficulty = 'easy' | 'normal' | 'hard'

export const BOT_DIFFICULTIES: readonly BotDifficulty[] = ['easy', 'normal', 'hard']

/**
 * 튜닝 값(= `normal`)에 곱하는 배수. 표가 절대값이 아니라 배수인 이유는 `?botEvery= ?botFind= ?botMiss=`가
 * 세 줄을 **한꺼번에** 움직여야 하기 때문이다 - 절대값 세 줄이면 URL 손잡이는 `normal`만 건드린다.
 * `originChance`는 난이도와 무관하다(원래 방 방문은 실력이 아니라 성향이다).
 */
const BOT_SCALE: Readonly<Record<BotDifficulty, Readonly<{ interval: number; find: number; miss: number }>>> = {
  /** 느리게, 덜 찾고, 더 틀린다. 첫 판의 상대다. */
  easy: { interval: 1.5, find: 0.6, miss: 1.4 },
  normal: { interval: 1, find: 1, miss: 1 },
  /** 빠르게, 잘 찾고, 덜 틀린다. 5분 안에 12개를 다 찾는 쪽이다. */
  hard: { interval: 0.7, find: 1.5, miss: 0.6 },
}

/** 배수 곱은 1을 넘을 수 있다. 확률이 [0, 1] 밖이면 `?botFind=`로 재는 값과 실제 행동이 어긋나므로 여기서 자른다. */
function clampChance(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/** 난이도 한 줄을 튜닝에서 편다. 결과는 라운드 내내 고정이다(`BotProfile` 주석). */
export function botProfileFor(difficulty: BotDifficulty, tuning: Tuning): BotProfile {
  const scale = BOT_SCALE[difficulty]
  return {
    intervalMs: tuning.botIntervalSeconds * scale.interval * MS_PER_SECOND,
    findChance: clampChance(tuning.botFindChance * scale.find),
    missChance: clampChance(tuning.botMissChance * scale.miss),
    originChance: clampChance(tuning.botOriginChance),
  }
}

/* --- 스트림 · 스케줄 -------------------------------------------------------------- */

/**
 * 봇 하나의 라운드 난수 스트림 시드. 방 시드(`seed`)·변화 시드(`${seed}:r${round}`)와 접두사로 갈라
 * 봇의 판단 수가 바뀌어도 방과 변화가 흔들리지 않는다(`changeGen`의 `:changes` 접미사와 같은 원리).
 */
export function botStreamSeed(seed: string, playerId: PlayerId, round: number): string {
  return `${seed}:bot:${playerId}:r${round}`
}

/**
 * `before → after`(수색 경과 ms) 사이에 판단 경계를 몇 번 넘었는가. 호스트가 이 수만큼 `cpuDecide`를 부른다.
 * 페이즈 시작(0)은 경계가 아니다 - 봇도 첫 N초는 "본다". 시계가 뒤로 갔거나 간격이 0 이하면 0이다(호스트 버그를
 * 봇의 난사로 바꾸지 않는다).
 */
export function cpuDecisionsDue(beforeMs: number, afterMs: number, profile: BotProfile): number {
  if (!(profile.intervalMs > 0) || afterMs <= beforeMs) return 0
  return Math.floor(afterMs / profile.intervalMs) - Math.floor(beforeMs / profile.intervalMs)
}

/* --- 판단 ------------------------------------------------------------------------ */

/** 한 판단이 소비하는 난수 개수. 분기와 무관하게 항상 이만큼이다(머리말). */
export const CPU_DECISION_ROLLS = 3

/** 쉬운 것부터. `difficulty`가 없는 변화(옛 픽스처)는 보통으로 읽는다. */
const DIFFICULTY_RANK: Readonly<Record<ChangeDifficulty, number>> = { easy: 0, normal: 1, hard: 2 }

function difficultyRank(change: Change): number {
  return DIFFICULTY_RANK[change.difficulty ?? 'normal']
}

/**
 * **내가 찾을 수 있는** 미발견 중 가장 쉬운 것. 같은 난이도면 생성 순서(앞)다 - `sort`는 안정적이라 같은 입력은 같은 답이다.
 * 자기 구획을 빼는 이유는 규칙이다(M11 규칙 4) - 넣으면 봇이 `own-zone` 거절을 쌓는다.
 * 봇이 쉬운 것부터 찾는 이유는 사람이 그렇기 때문이다: 어려운 것이 마지막까지 남아야 리빌의 "저걸 못 봤다고???"가 산다.
 */
function easiestUnfound(state: MatchState, id: PlayerId): Change | undefined {
  const unfound = findableUnfound(state, id)
  if (unfound.length === 0) return undefined
  return [...unfound].sort((a, b) => difficultyRank(a) - difficultyRank(b))[0]
}

/**
 * 변화를 사격 대상으로. 제거는 빈 자리(프록시), 나머지는 물건 그 자체다 - `hunt.matchesTargetKind`가 요구하는 짝이고,
 * 여기서 종류를 틀리면 봇은 정답을 겨누고도 `miss`로 탄환을 잃는다.
 */
function hitFor(change: Change): HitTarget {
  return change.verb === 'remove'
    ? { kind: 'proxy', instanceId: change.targetId }
    : { kind: 'object', instanceId: change.targetId }
}

/**
 * CPU 좌석의 판단 한 번. 우선순위가 규칙이다:
 *  0. 바꾸기 중이면 [다 했다] 한 장, **롤 없이**(아래 주석) - 그 구획은 마감 채움이 생성기로 채운다
 *  1. 술래로 행동할 수 없으면(수색 밖 · 관전 · 프로필 없음) 조용하다 - 리듀서가 거절하겠지만 큐를 어지럽히지 않는다
 *  2. 원래 방 안이면 나온다 - 한 간격(N초) 본 것으로 충분하다. 체류 상한까지 눌러앉는 봇은 문을 막는 벽이다
 *  3. 잔탄 0이면 탄창을 쓴다. 튜닝을 묻지 않는 이유: 잔탄 0인 채로 관전이 아닌 술래는 `matchShoot.onMiss`가
 *     "탄창 검사 먼저"로 살려 둔 사람이라 **정의상 탄창을 쓸 수 있다.** 여기서 `canReload`를 다시 부르면 조건이 두 벌이다
 *  4. `originChance`로 원래 방. **문이 열려 있을 때만**(`canEnterOrigin`) - 줄에 서는 봇은 유예를 놓치기만 한다
 *  5. `findChance`로 미발견 중 가장 쉬운 것을 맞힌다. 미발견 목록에서 고르므로 `already-found`는 구조적으로 불가능하다
 *  6. `missChance`로 허공을 쏜다(`hit: null`). 탄환을 잃는 인간미(9.1절)
 *  7. 아니면 본다(빈 배열)
 *
 * 세 롤은 순서와 무관하게 **먼저 전부** 뽑는다(머리말 - 0번을 지난 뒤부터다). 그래서 4~6의 확률은 각 롤 하나씩이고 서로 독립이다 -
 * 앞 분기가 잡히면 뒤 롤은 버려진다(오답은 "찾지 못한 판단"에서만 난다).
 */
export function cpuDecide(state: MatchState, playerId: PlayerId, rng: Rng): readonly PlayerEvent[] {
  /*
   * 바꾸기 페이즈는 **롤을 하나도 뽑지 않는다.** 소비량 고정 계약(머리말)은 수색 판단의 것이다 - 바꾸기에서도 세 롤을
   * 먼저 뽑으면 라운드 스트림(`matchHost.botRngFor`는 라운드가 같으면 같은 Rng를 이어 쓴다)이 그만큼 밀리고,
   * 그 개수는 "사람이 [다 했다]를 언제 눌렀는가"(= 바꾸기가 몇 초였는가)의 함수다. 그러면 같은 `?seed=`의 두 판이
   * 다른 봇 사격 열을 낸다. 수색 스트림은 라운드 시작에서 인덱스 0으로 서야 한다.
   */
  if (state.phase === 'change') {
    // 이웃도 자기 구획을 갖지만 손으로 고치지 않는다 - 마감 채움이 생성기로 채운다(M11 규칙 7·8).
    return canUseDesk(state, playerId) ? [{ type: 'commitChanges', wager: null }] : []
  }

  const rollOrigin = rng.next()
  const rollFind = rng.next()
  const rollMiss = rng.next()

  const player = playerOf(state, playerId)
  if (!isActiveHunter(state, player) || player.bot === null) return []
  const profile = player.bot

  if (player.origin.isInOriginRoom) return [{ type: 'leaveOrigin' }]
  if (player.ammo <= 0) return [{ type: 'useMagazine' }]

  if (rollOrigin < profile.originChance && canEnterOrigin(state, playerId)) return [{ type: 'requestOrigin' }]

  if (rollFind < profile.findChance) {
    const target = easiestUnfound(state, playerId)
    if (target !== undefined) return [{ type: 'shoot', hit: hitFor(target) }]
  }

  if (rollMiss < profile.missChance) return [{ type: 'shoot', hit: null }]

  return []
}
